/**
 * The accessibility plumbing every screen shares: the two aria-live voices,
 * F6 between regions in the order of DESIGN.md §3.1, a focus trap for sheets,
 * and the 600 ms hold that stands in for a confirm click under a pointer.
 *
 * The decisions (which region is next, where Tab lands, how far a hold has
 * got) are pure functions; the DOM adapters around them stay thin.
 */

import type { Announcer, Region, Unsub } from '../contracts';

// ---------- the two voices ----------

/**
 * Polite for lifts, targets and receipts; assertive for refusals. The text is
 * cleared first and set a moment later, so the same sentence twice in a row
 * is still read twice.
 */
export const createAnnouncer = (
  polite: HTMLElement,
  assertive: HTMLElement,
  later: (fn: () => void) => unknown = (fn) => setTimeout(fn, 40),
): Announcer => {
  const say = (el: HTMLElement, text: string): void => {
    el.textContent = '';
    later(() => { el.textContent = text; });
  };
  return {
    polite: (text) => say(polite, text),
    assertive: (text) => say(assertive, text),
  };
};

// ---------- regions and F6 ----------

/** §3.1: Forge, Stage/Field, Roster, River, Gates, Inspector, Wire, Director, Gallery; then the rest (the hand's order too). */
export const REGION_ORDER: readonly Region[] = [
  'forge', 'stage', 'field', 'roster', 'river', 'gates', 'inspector', 'wire', 'director', 'gallery', 'deck', 'mcp', 'account', 'connect',
];

const rank = (r: Region): number => {
  const i = REGION_ORDER.indexOf(r);
  return i < 0 ? REGION_ORDER.length : i;
};

/** The region F6 (dir 1) or Shift+F6 (dir -1) moves to, among those on screen. */
export const regionCycle = (present: readonly Region[], current: Region | null, dir: 1 | -1): Region | null => {
  const order = [...new Set(present)].sort((a, b) => rank(a) - rank(b));
  if (order.length === 0) return null;
  const at = current === null ? -1 : order.indexOf(current);
  if (at < 0) return dir === 1 ? order[0]! : order[order.length - 1]!;
  return order[(at + dir + order.length) % order.length]!;
};

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Shown, and not inside something hidden or inert (a panel slid away: F6 and focus pass it by). */
export const isShown = (el: Element): boolean => !el.closest('[hidden], [inert]') && el.getClientRects().length > 0;

export const focusablesIn = (root: ParentNode): HTMLElement[] =>
  [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(isShown);

/**
 * A modal sheet is up (a confirm, a secret shown once, the palette): the page's
 * own shortcuts keep out, so none pulls focus from under it.
 */
export const modalOpen = (doc: Document): boolean =>
  !!doc.activeElement?.closest('[aria-modal="true"]') || [...doc.querySelectorAll('[aria-modal="true"]')].some(isShown);

/** Keys typed into a field are the field's, never a shortcut's. */
export const isTyping = (target: EventTarget | null): boolean => {
  if (!target || typeof (target as Element).closest !== 'function') return false;
  const el = target as HTMLElement;
  return el.isContentEditable || el.closest('input, textarea, select, [contenteditable="true"]') !== null;
};

const firstShown = (root: ParentNode, selector: string): HTMLElement | null =>
  [...root.querySelectorAll<HTMLElement>(selector)].find(isShown) ?? null;

/**
 * F6 and Shift+F6 cycle the regions on screen: every element with
 * `data-region` joins, ordered by REGION_ORDER. Focus lands on the region's
 * shown `[data-region-focus]`, else its roving `[tabindex="0"]`, else its
 * first focusable, else the region itself.
 *
 * This is the page's only F6. It listens in the capture phase and stops the
 * key there, so the hand's own region walk (bound on the app root, and able
 * to see only regions that hold something it can carry) never moves focus a
 * second time. Inside a modal sheet F6 does nothing: the sheet keeps focus.
 */
export const bindRegions = (doc: Document, announce: Announcer): Unsub => {
  const onKey = (e: KeyboardEvent): void => {
    if (e.key !== 'F6' || e.defaultPrevented) return;
    if (doc.activeElement?.closest('[aria-modal="true"]')) return;
    const els = [...doc.querySelectorAll<HTMLElement>('[data-region]')].filter(isShown);
    const present = els.map((el) => el.dataset.region as Region);
    const here = (doc.activeElement?.closest('[data-region]') as HTMLElement | null)?.dataset.region as Region | undefined;
    const next = regionCycle(present, here ?? null, e.shiftKey ? -1 : 1);
    if (!next) return;
    e.preventDefault();
    e.stopPropagation();
    const el = els.find((x) => x.dataset.region === next)!;
    const into = firstShown(el, '[data-region-focus]') ?? firstShown(el, '[tabindex="0"]') ?? focusablesIn(el)[0] ?? null;
    if (into) into.focus();
    else {
      if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
      el.focus();
    }
    announce.polite(el.getAttribute('aria-label') ?? next);
  };
  doc.addEventListener('keydown', onKey, true);
  return () => doc.removeEventListener('keydown', onKey, true);
};

// ---------- focus trap ----------

/** Where Tab (or Shift+Tab) goes among `count` focusables from `index`, wrapping. -1 when there are none. */
export const trapStep = (count: number, index: number, back: boolean): number => {
  if (count <= 0) return -1;
  if (index < 0) return back ? count - 1 : 0;
  return back ? (index === 0 ? count - 1 : index - 1) : (index === count - 1 ? 0 : index + 1);
};

/** Keep Tab inside `container` until the returned function is called. */
export const trapFocus = (container: HTMLElement): Unsub => {
  const onKey = (e: KeyboardEvent): void => {
    if (e.key !== 'Tab') return;
    const items = focusablesIn(container);
    const next = trapStep(items.length, items.indexOf(document.activeElement as HTMLElement), e.shiftKey);
    e.preventDefault();
    if (next >= 0) items[next]!.focus();
  };
  container.addEventListener('keydown', onKey);
  return () => container.removeEventListener('keydown', onKey);
};

// ---------- the hold ----------

export const HOLD_MS = 600;

/** How far a press that began at `startedAt` has got, 0 to 1. */
export const holdProgress = (startedAt: number | null, now: number, ms = HOLD_MS): number =>
  startedAt === null ? 0 : Math.max(0, Math.min(1, (now - startedAt) / ms));

/**
 * A button a pointer must hold for `ms` before it counts; a click without a
 * pointer press (Enter, Space, assistive technology) counts at once. The
 * button's `--hold` property runs 0 → 1 for the CSS to draw.
 */
export const bindHold = (
  button: HTMLElement,
  onDone: () => void,
  opts: { ms?: number; onHint?: () => void; now?: () => number } = {},
): Unsub => {
  const ms = opts.ms ?? HOLD_MS;
  const now = opts.now ?? (() => performance.now());
  let started: number | null = null;
  let frame = 0;
  let done = false;
  const paint = (p: number): void => { button.style.setProperty('--hold', String(p)); };
  const finish = (): void => {
    if (done) return;
    done = true;
    cancelAnimationFrame(frame);
    paint(1);
    onDone();
  };
  const tick = (): void => {
    const p = holdProgress(started, now(), ms);
    paint(p);
    if (p >= 1) finish();
    else frame = requestAnimationFrame(tick);
  };
  const down = (e: PointerEvent): void => {
    if (e.button !== 0 || done) return;
    started = now();
    try { button.setPointerCapture(e.pointerId); } catch { /* not every element takes capture */ }
    frame = requestAnimationFrame(tick);
  };
  const up = (): void => {
    if (done || started === null) return;
    const p = holdProgress(started, now(), ms);
    started = null;
    cancelAnimationFrame(frame);
    if (p >= 1) finish();
    else {
      paint(0);
      opts.onHint?.();
    }
  };
  const click = (e: MouseEvent): void => {
    // detail 0: the click came from a key or from assistive technology, not a pointer press.
    if (e.detail === 0) finish();
  };
  button.addEventListener('pointerdown', down);
  button.addEventListener('pointerup', up);
  button.addEventListener('pointercancel', up);
  button.addEventListener('lostpointercapture', up);
  button.addEventListener('click', click);
  return () => {
    cancelAnimationFrame(frame);
    button.removeEventListener('pointerdown', down);
    button.removeEventListener('pointerup', up);
    button.removeEventListener('pointercancel', up);
    button.removeEventListener('lostpointercapture', up);
    button.removeEventListener('click', click);
  };
};

/** The viewer asked the system for less motion. */
/**
 * Whether to move less now: the in-app ≈ switch wins either way (fx's
 * `data-motion`, the shell's `data-reduced`), else the system setting. The one
 * reading every screen uses, so the field and the Deck never disagree.
 */
export const reducedMotion = (doc: Document | undefined = globalThis.document): boolean => {
  const el = doc?.documentElement;
  if (!el) return false;
  if (el.dataset.motion === 'reduced' || el.hasAttribute('data-reduced')) return true;
  if (el.dataset.motion === 'full') return false;
  return prefersReducedMotion(doc.defaultView ?? undefined);
};

export const prefersReducedMotion = (win: Window | undefined = globalThis.window): boolean => {
  try {
    return win?.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  } catch {
    return false;
  }
};
